import {useCallback, useEffect, useRef} from 'react';
import {useHistory} from 'src/rerouter';
import type {UseChatOptions, UseChat} from './use-chat';
import {useChat} from './use-chat';
import type {Part} from 'src/components/agentic-ui';
import {runPreSendCollectors} from 'src/components/agentic-ui/context-collectors';


export type UseAgenticSuggestionsOptions = Omit<
  UseChatOptions,
  'getStreamUrl' | 'collectMessageContext'
> & {
  /**
   * When false, the hook will not auto-run. You can still call `run()` manually.
   * Default: true
   */
  enabled?: boolean;
  /**
   * Auto-start on mount / when enabled flips to true.
   * Default: true
   */
  autoStart?: boolean;
  /**
   * Optional key to trigger a re-run when it changes (e.g. page id).
   * When the key changes and `autoStart` is true, the hook will run again.
   */
  runKey?: string | number | null;
  /**
   * Request payload to send when starting the SSE stream.
   * If omitted, sends an empty parts list.
   */
  parts?: Part[];
  meta?: Record<string, unknown>;
  /**
   * Do not insert a local user message before streaming begins.
   * Default: true for suggestions.
   */
  silent?: boolean;
  /**
   * Clear existing messages before starting a new run.
   * Default: false
   */
  clearBeforeRun?: boolean;
  getStreamUrl?: (() => Promise<string>) | (() => string);
  collectMessageContext?: () => Promise<Record<string, unknown>>;
};

export type UseAgenticSuggestions = UseChat & {
  /** Starts (or re-starts) the suggestions stream. */
  run: () => Promise<string>;
  /** Resets "has run" and runs again. */
  refresh: () => Promise<string>;
};

/**
 * Eager, suggestion-style wrapper around `useChat`.
 * Reuses the same SSE message handling but starts the stream automatically.
 */
export function useAgenticSuggestions(
  opts: UseAgenticSuggestionsOptions,
): UseAgenticSuggestions {
  const history = useHistory();
  const {
    enabled = true,
    autoStart = true,
    runKey = null,
    parts = [
      {
        type: 'plain_text',
        data: ``,
      },
    ],
    meta = {},
    silent = true,
    clearBeforeRun = true,
    getStreamUrl = () =>
      Promise.resolve(`/grace/api/v1/conversation/conversation-starters`),
    collectMessageContext = () => runPreSendCollectors({history}, undefined),

    ...chatOpts
  } = opts;

  const chat = useChat({...chatOpts, getStreamUrl, collectMessageContext});

  const hasRunRef = useRef(false);
  const lastRunKeyRef = useRef<string | number | null>(runKey);

  if (lastRunKeyRef.current !== runKey) {
    lastRunKeyRef.current = runKey;
    hasRunRef.current = false;
  }

  const run = useCallback(async () => {
    if (clearBeforeRun) {
      chat.clear();
    }
    hasRunRef.current = true;
    return chat.sendMessage(parts, meta, {localEcho: !silent});
  }, [chat, clearBeforeRun, meta, parts, silent]);

  const refresh = useCallback(async () => {
    hasRunRef.current = false;
    return run();
  }, [run]);

  useEffect(() => {
    if (!enabled || !autoStart || hasRunRef.current) {
      return;
    }
    void run();
  }, [enabled, autoStart, run]);

  return {
    ...chat,
    run,
    refresh,
  };
}
